import { forwardRef, memo } from "react";
import Moment from "react-moment";
import { formatCurrency, getTransactionFieldRows } from "./SCRV2InvoiceDetailHelpers";

export const SCR_V2_INVOICE_DETAIL_PRINT_STYLES = `
.scr-v2-invoice-print-view {
  font-family: Arial, Helvetica, sans-serif;
  color: #0f172a;
  width: 100%;
  box-sizing: border-box;
}
.scr-v2-invoice-print-view * { box-sizing: border-box; }
.scr-v2-invoice-print-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}
.scr-v2-invoice-print-header__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}
.scr-v2-invoice-print-header__meta {
  text-align: right;
  font-size: 13px;
  color: #475569;
}
.scr-v2-invoice-print-status {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}
.scr-v2-invoice-print-status--finalized {
  background: #dcfce7;
  color: #166534;
}
.scr-v2-invoice-print-status--open {
  background: #fef3c7;
  color: #92400e;
}
.scr-v2-invoice-print-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}
.scr-v2-invoice-print-panel {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
  break-inside: avoid;
}
.scr-v2-invoice-print-panel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.scr-v2-invoice-print-panel__title {
  font-size: 13px;
  font-weight: 700;
}
.scr-v2-invoice-print-panel__badge {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 2px 8px;
}
.scr-v2-invoice-print-panel__body { padding: 10px; }
.scr-v2-invoice-print-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.scr-v2-invoice-print-fields--tx {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
}
.scr-v2-invoice-print-field__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 2px;
}
.scr-v2-invoice-print-field__value {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  word-break: break-word;
}
.scr-v2-invoice-print-amounts {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-top: 10px;
}
.scr-v2-invoice-print-amount {
  text-align: center;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 6px;
}
.scr-v2-invoice-print-amount__label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 2px;
}
.scr-v2-invoice-print-amount__value {
  font-size: 14px;
  font-weight: 700;
}
.scr-v2-invoice-print-transactions { display: grid; gap: 8px; }
.scr-v2-invoice-print-tx {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
  break-inside: avoid;
}
.scr-v2-invoice-print-tx__header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.scr-v2-invoice-print-tx__index {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.scr-v2-invoice-print-tx__main { flex: 1; min-width: 0; }
.scr-v2-invoice-print-tx__title { font-size: 13px; font-weight: 700; }
.scr-v2-invoice-print-tx__date { font-size: 11px; color: #64748b; margin-top: 2px; }
.scr-v2-invoice-print-tx__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
}
.scr-v2-invoice-print-tx__badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 999px;
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
}
.scr-v2-invoice-print-tx__badge--danger {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
}
.scr-v2-invoice-print-empty {
  font-size: 12px;
  color: #64748b;
  text-align: center;
  padding: 12px 0;
}
.scr-v2-invoice-print-footer {
  margin-top: 12px;
  font-size: 11px;
  color: #64748b;
  text-align: right;
}
.scr-v2-invoice-print-text-primary { color: #e84d15; }
.scr-v2-invoice-print-text-success { color: #059669; }
.scr-v2-invoice-print-text-danger { color: #dc2626; }
`;

const PrintField = ({ label, value }) => (
  <div className="scr-v2-invoice-print-field">
    <div className="scr-v2-invoice-print-field__label">{label}</div>
    <div className="scr-v2-invoice-print-field__value">{value ?? "-"}</div>
  </div>
);

const PrintPanel = ({ title, badge, children }) => (
  <div className="scr-v2-invoice-print-panel">
    <div className="scr-v2-invoice-print-panel__header">
      <span className="scr-v2-invoice-print-panel__title">{title}</span>
      {badge ? <span className="scr-v2-invoice-print-panel__badge">{badge}</span> : null}
    </div>
    <div className="scr-v2-invoice-print-panel__body">{children}</div>
  </div>
);

const formatDate = (value) =>
  value ? <Moment format="DD-MM-YYYY">{value}</Moment> : "-";

const formatDateTime = (value) =>
  value ? <Moment format="DD-MM-YYYY HH:mm">{value}</Moment> : "-";

const SCRV2InvoiceDetailPrint = forwardRef(
  ({ detail, report = {}, rowContext = {} }, ref) => {
    if (!detail) {
      return null;
    }

    const printedAt = new Date();
    const invoiceNo =
      detail.vizproInvoiceNumber ||
      detail.invoiceNumber ||
      rowContext.invoiceNo ||
      "-";
    const totals = detail.totals || {};
    const transactions = detail.transactions || [];
    const varianceAmount = Number(totals.varianceAmount || 0);

    return (
      <div ref={ref} className="scr-v2-invoice-print-view">
        <style>{SCR_V2_INVOICE_DETAIL_PRINT_STYLES}</style>

        <div className="scr-v2-invoice-print-header">
          <h1 className="scr-v2-invoice-print-header__title">SCR Invoice Detail</h1>
          <div className="scr-v2-invoice-print-header__meta">
            <span>{invoiceNo}</span>
            {report.scrNo ? <span> · {report.scrNo}</span> : null}
            {report.isFinalized != null ? (
              <span
                className={`scr-v2-invoice-print-status ${
                  report.isFinalized
                    ? "scr-v2-invoice-print-status--finalized"
                    : "scr-v2-invoice-print-status--open"
                }`}
              >
                {report.isFinalized ? "Finalized" : "Open"}
              </span>
            ) : null}
          </div>
        </div>

        <div className="scr-v2-invoice-print-grid">
          <PrintPanel title="Shop Information">
            <div className="scr-v2-invoice-print-fields">
              <PrintField label="Shop Name" value={rowContext.shopName} />
              <PrintField label="DLM QR Code" value={report.deliveryQRCode} />
              <PrintField label="Shop Code" value={rowContext.vizShopCode} />
              <PrintField label="DLM Name" value={report.dlmName || report.dlmUserName} />
            </div>
          </PrintPanel>

          <PrintPanel
            title="Invoice Information"
            badge={
              Number(detail.participationMode) === 1
                ? detail.participationModeLabel || "Recovery"
                : null
            }
          >
            <div className="scr-v2-invoice-print-fields">
              <PrintField label="Invoice No" value={invoiceNo} />
              <PrintField label="Invoice Date" value={formatDate(detail.invoiceDate)} />
              {detail.dueDate ? (
                <PrintField label="Due Date" value={formatDate(detail.dueDate)} />
              ) : null}
              {Number(totals.totalCreditAmount || 0) !== 0 ? (
                <PrintField label="Credit" value={formatCurrency(totals.totalCreditAmount)} />
              ) : null}
              {Number(totals.totalRecoveryAmount || 0) !== 0 ? (
                <PrintField label="Recovery" value={formatCurrency(totals.totalRecoveryAmount)} />
              ) : null}
              {Number(totals.totalAdjustmentAmount || 0) !== 0 ? (
                <PrintField
                  label="Adjustments"
                  value={formatCurrency(totals.totalAdjustmentAmount)}
                />
              ) : null}
              {Number(totals.totalReversalAmount || 0) !== 0 ? (
                <PrintField
                  label="Reversals"
                  value={formatCurrency(totals.totalReversalAmount)}
                />
              ) : null}
            </div>
            <div className="scr-v2-invoice-print-amounts">
              <div className="scr-v2-invoice-print-amount">
                <div className="scr-v2-invoice-print-amount__label">Amount</div>
                <div className="scr-v2-invoice-print-amount__value scr-v2-invoice-print-text-primary">
                  {formatCurrency(totals.scopedDueAmount)}
                </div>
              </div>
              <div className="scr-v2-invoice-print-amount">
                <div className="scr-v2-invoice-print-amount__label">Received</div>
                <div className="scr-v2-invoice-print-amount__value scr-v2-invoice-print-text-success">
                  {formatCurrency(totals.scopedReceivedAmount)}
                </div>
              </div>
              <div className="scr-v2-invoice-print-amount">
                <div className="scr-v2-invoice-print-amount__label">Outstanding</div>
                <div
                  className={`scr-v2-invoice-print-amount__value ${
                    varianceAmount > 0
                      ? "scr-v2-invoice-print-text-danger"
                      : varianceAmount < 0
                        ? "scr-v2-invoice-print-text-success"
                        : ""
                  }`}
                >
                  {formatCurrency(varianceAmount)}
                </div>
              </div>
            </div>
          </PrintPanel>
        </div>

        <PrintPanel title="Transactions" badge={`${transactions.length}`}>
          {transactions.length === 0 ? (
            <div className="scr-v2-invoice-print-empty">No transaction details available.</div>
          ) : (
            <div className="scr-v2-invoice-print-transactions">
              {transactions.map((transaction, index) => {
                const fieldRows = getTransactionFieldRows(transaction);
                return (
                  <div
                    key={`${transaction.transactionCategory}-${transaction.id}-${index}`}
                    className="scr-v2-invoice-print-tx"
                  >
                    <div className="scr-v2-invoice-print-tx__header">
                      <span className="scr-v2-invoice-print-tx__index">{index + 1}</span>
                      <div className="scr-v2-invoice-print-tx__main">
                        <div className="scr-v2-invoice-print-tx__title">
                          {transaction.paymentTypeMethod ||
                            transaction.transactionCategory ||
                            "Transaction"}
                        </div>
                        <div className="scr-v2-invoice-print-tx__date">
                          {formatDateTime(transaction.paymentDate)}
                        </div>
                      </div>
                      <div className="scr-v2-invoice-print-tx__meta">
                        <span className="scr-v2-invoice-print-text-success">
                          {formatCurrency(transaction.amount)}
                        </span>
                        {transaction.transactionCategory ? (
                          <span className="scr-v2-invoice-print-tx__badge">
                            {transaction.transactionCategory}
                          </span>
                        ) : null}
                        {transaction.isReversed ? (
                          <span className="scr-v2-invoice-print-tx__badge scr-v2-invoice-print-tx__badge--danger">
                            Reversed
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {fieldRows.length > 0 ? (
                      <div className="scr-v2-invoice-print-fields scr-v2-invoice-print-fields--tx">
                        {fieldRows.map((field) => (
                          <PrintField
                            key={`${transaction.id}-${field.label}`}
                            label={field.label}
                            value={
                              field.isFile
                                ? "Attachment"
                                : field.isDateTime && field.value
                                  ? formatDateTime(field.value)
                                  : field.isDate && field.value
                                    ? formatDate(field.value)
                                    : field.value ?? "-"
                            }
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </PrintPanel>

        <div className="scr-v2-invoice-print-footer">
          Printed: <Moment format="DD-MM-YYYY HH:mm">{printedAt}</Moment>
        </div>
      </div>
    );
  }
);

SCRV2InvoiceDetailPrint.displayName = "SCRV2InvoiceDetailPrint";

export default memo(SCRV2InvoiceDetailPrint);
